/**
 * ChoirMasterPasswordTab — Main Admin only.
 * Sets one master password for ALL approved choir members and emails
 * every choir member the new password when it's rotated.
 *
 * The Main Admin must rotate this password every 30 days.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  KeyRound, Loader2, Lock, MailCheck, ShieldCheck, AlertTriangle, RefreshCw,
  Eye, EyeOff, Calendar, Send,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const authHeaders = () => {
  const t = localStorage.getItem('adminToken') || localStorage.getItem('admin_token') || '';
  return t ? { 'x-admin-token': t } : {};
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

export default function ChoirMasterPasswordTab({ isMainAdmin }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState('');

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/auth/member/admin/choir-master-password/status`, {
        headers: authHeaders(),
      });
      setStatus(res.data);
    } catch (e) {
      setStatus(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const submit = async () => {
    setError('');
    if (!password) { setError('Please type a password.'); return; }
    if (password !== confirm) { setError('Confirmation does not match.'); return; }
    if (!window.confirm(
      `This will:\n\n` +
      `• Set a new master password for ALL choir members.\n` +
      `• Wipe any old per-member passwords.\n` +
      `• Email every choir member with the new password.\n\n` +
      `Continue?`
    )) return;

    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/auth/member/admin/choir-master-password`,
        { password },
        { headers: authHeaders() },
      );
      setLastResult(res.data);
      setPassword('');
      setConfirm('');
      await loadStatus();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to set master password.');
    }
    setSubmitting(false);
  };

  const days = status?.days_since_change;
  const isDue = status?.is_due;
  const isSet = status?.is_set;

  const statusBadge = useMemo(() => {
    if (!isSet) return { label: 'NOT YET SET', cls: 'bg-rose-100 text-rose-700 border-rose-200' };
    if (isDue) return { label: 'ROTATION DUE', cls: 'bg-red-100 text-red-700 border-red-200' };
    if (days >= 20) return { label: 'ROTATION SOON', cls: 'bg-amber-100 text-amber-700 border-amber-200' };
    return { label: 'UP TO DATE', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
  }, [isSet, isDue, days]);

  // Locked screen for non-main-admins
  if (!isMainAdmin) {
    return (
      <div className="bg-white border border-slate-200 rounded-3xl p-10 text-center" data-testid="master-password-locked">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
          <Lock className="w-8 h-8 text-slate-400" />
        </div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">Restricted to Main Admin</h3>
        <p className="text-slate-500 max-w-md mx-auto">
          The choir master password can only be set or rotated by the Main Admin account.
          If you need this changed, please reach out to the Main Admin.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="master-password-tab">
      {/* Status card */}
      <div className="rounded-3xl overflow-hidden border border-slate-200">
        <div className="px-6 py-5 bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 text-white flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-white/15">
            <KeyRound className="w-7 h-7" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold leading-tight">Choir Master Password</h2>
            <p className="text-violet-100 text-sm">
              One password for every approved choir member — rotated monthly by the Main Admin.
            </p>
          </div>
          <span
            className={`text-[11px] font-black tracking-wider px-3 py-1.5 rounded-full border ${statusBadge.cls}`}
            data-testid="master-password-status-badge"
          >
            {statusBadge.label}
          </span>
        </div>

        <div className="bg-white p-6 grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="space-y-1">
            <p className="text-[11px] font-bold text-slate-400 tracking-wider">LAST CHANGED</p>
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            ) : (
              <>
                <p className="text-base font-bold text-slate-800" data-testid="master-password-last-changed">
                  {formatDate(status?.set_at)}
                </p>
                {days !== null && days !== undefined && (
                  <p className="text-xs text-slate-500">{days} day{days === 1 ? '' : 's'} ago by {status?.set_by || 'unknown'}</p>
                )}
              </>
            )}
          </div>
          <div className="space-y-1">
            <p className="text-[11px] font-bold text-slate-400 tracking-wider">NEXT ROTATION</p>
            <p className="text-base font-bold text-slate-800">
              {isSet && days !== null ? `${Math.max(0, 30 - days)} day${Math.max(0, 30 - days) === 1 ? '' : 's'} left` : 'Set now'}
            </p>
            <p className="text-xs text-slate-500">Rotate every 30 days</p>
          </div>
          <div className="space-y-1">
            <p className="text-[11px] font-bold text-slate-400 tracking-wider">CHOIR MEMBERS</p>
            <p className="text-base font-bold text-slate-800" data-testid="master-password-choir-count">
              {status?.choir_count ?? 0}
            </p>
            <p className="text-xs text-slate-500">Will receive the new password by email.</p>
          </div>
        </div>
      </div>

      {/* Delivery breakdown + Resend-to-failed */}
      {isSet && (
        <DeliveryBreakdown
          status={status}
          onResendComplete={loadStatus}
        />
      )}

      {/* Rotation due banner */}
      {isDue && (
        <motion.div
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-3 bg-red-50 border-2 border-red-200 rounded-2xl p-4"
          data-testid="master-password-due-banner"
        >
          <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-bold text-red-900">
              {isSet ? "It's time to rotate the choir master password" : 'No master password has been set yet'}
            </p>
            <p className="text-sm text-red-800">
              {isSet
                ? `The password was last changed ${days} day${days === 1 ? '' : 's'} ago. Please set a new password below — every choir member will be emailed automatically.`
                : 'Set a password below to enable login for all approved choir members. Each member will receive the new password by email.'}
            </p>
          </div>
        </motion.div>
      )}

      {/* Form */}
      <div className="bg-white rounded-3xl border border-slate-200 p-6 space-y-5">
        <div className="flex items-center gap-2 text-slate-800">
          <ShieldCheck className="w-5 h-5 text-violet-600" />
          <h3 className="font-bold">Set / Rotate Password</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs font-bold text-slate-500 tracking-wider">NEW PASSWORD</span>
            <div className="relative mt-1.5">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="master-password-input"
                placeholder="Type the new password (no rules)"
                className="w-full px-4 py-3 pr-12 rounded-xl border border-slate-200 bg-slate-50 font-mono text-base focus:outline-none focus:border-violet-500 focus:bg-white"
              />
              <button
                type="button"
                onClick={() => setShowPassword(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                data-testid="master-password-toggle-visibility"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </label>
          <label className="block">
            <span className="text-xs font-bold text-slate-500 tracking-wider">CONFIRM PASSWORD</span>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="master-password-confirm"
              placeholder="Re-enter the password"
              className="w-full mt-1.5 px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 font-mono text-base focus:outline-none focus:border-violet-500 focus:bg-white"
            />
          </label>
        </div>

        {error && (
          <div className="rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm px-4 py-3" data-testid="master-password-error">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between gap-4 pt-2">
          <p className="text-xs text-slate-500">
            <span className="font-bold text-slate-700">What happens next:</span>{' '}
            Every choir member with an email on file will receive the new password and a confidentiality reminder.
            Old per-member passwords are wiped.
          </p>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !password || password !== confirm}
            data-testid="master-password-submit"
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-violet-500/20 whitespace-nowrap"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {submitting ? 'Sending…' : 'Set & Email All Choir'}
          </button>
        </div>
      </div>

      {/* Last result */}
      {lastResult && (
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-start gap-3"
          data-testid="master-password-result"
        >
          <MailCheck className="w-6 h-6 text-emerald-600 mt-0.5" />
          <div className="flex-1">
            <p className="font-bold text-emerald-900">Master password updated</p>
            <p className="text-sm text-emerald-800">
              <strong>{lastResult.sent}</strong> email{lastResult.sent === 1 ? '' : 's'} sent
              {lastResult.failed > 0 && (<> · <strong>{lastResult.failed}</strong> failed</>)}
              {' '}of <strong>{lastResult.total_choir_members}</strong> total choir member{lastResult.total_choir_members === 1 ? '' : 's'}.
            </p>
            <p className="text-xs text-emerald-700 mt-1">
              <Calendar className="inline w-3 h-3 mr-1" />
              Next rotation due: {formatDate(new Date(Date.now() + 30 * 86400000).toISOString())}
            </p>
          </div>
          <button onClick={loadStatus} className="text-emerald-700 hover:text-emerald-900" title="Refresh">
            <RefreshCw className="w-5 h-5" />
          </button>
        </motion.div>
      )}
    </div>
  );
}

/**
 * DeliveryBreakdown — shows how the most recent rotation actually landed
 * (sent / failed / stale / never) and lets the Main Admin re-send the
 * current password ONLY to members who didn't get it.
 */
function DeliveryBreakdown({ status, onResendComplete }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const sent = status?.delivery_sent ?? 0;
  const failed = status?.delivery_failed ?? 0;
  const stale = status?.delivery_stale ?? 0;
  const never = status?.delivery_never ?? 0;
  const needsResend = status?.delivery_needs_resend ?? 0;

  const submit = async () => {
    setErr('');
    if (!pw) { setErr('Type the current master password to confirm.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/auth/member/admin/choir-master-password/resend-failed`,
        { password: pw },
        { headers: authHeaders() },
      );
      setResult(res.data);
      setPw('');
      onResendComplete && onResendComplete();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Resend failed.');
    }
    setSubmitting(false);
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-200 p-6 space-y-4" data-testid="delivery-breakdown">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <MailCheck className="w-5 h-5 text-violet-600" /> Last rotation delivery
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">How each choir member's email landed for the most recent password change.</p>
        </div>
        {needsResend > 0 && !open && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="open-resend-failed"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 shadow"
          >
            <Send className="w-4 h-4" /> Resend to {needsResend} member{needsResend === 1 ? '' : 's'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-3">
          <p className="text-[10px] font-black text-emerald-700 tracking-wider">DELIVERED</p>
          <p className="text-2xl font-black text-emerald-800">{sent}</p>
        </div>
        <div className="rounded-2xl bg-rose-50 border border-rose-200 px-4 py-3">
          <p className="text-[10px] font-black text-rose-700 tracking-wider">FAILED</p>
          <p className="text-2xl font-black text-rose-800">{failed}</p>
        </div>
        <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3">
          <p className="text-[10px] font-black text-amber-700 tracking-wider">STALE</p>
          <p className="text-2xl font-black text-amber-800">{stale}</p>
        </div>
        <div className="rounded-2xl bg-slate-50 border border-slate-200 px-4 py-3">
          <p className="text-[10px] font-black text-slate-600 tracking-wider">NEVER SENT</p>
          <p className="text-2xl font-black text-slate-700">{never}</p>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border-2 border-rose-200 bg-rose-50/50 p-4 space-y-3"
        >
          <p className="text-sm text-rose-900">
            To re-send the <strong>current</strong> password to the {needsResend} member{needsResend === 1 ? '' : 's'} who haven't received it,
            type the current password below. Nothing on the choir side will change — this just retries the email.
          </p>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            data-testid="resend-failed-password-input"
            placeholder="Type the current master password"
            className="w-full px-4 py-3 rounded-xl border border-rose-200 bg-white font-mono focus:outline-none focus:border-rose-500"
          />
          {err && <p className="text-sm text-rose-700" data-testid="resend-failed-error">{err}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setOpen(false); setPw(''); setErr(''); }}
              className="flex-1 px-4 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-semibold hover:bg-slate-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !pw}
              data-testid="resend-failed-submit"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-rose-500 to-red-600 text-white font-bold disabled:opacity-50"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {submitting ? 'Sending…' : `Resend to ${needsResend}`}
            </button>
          </div>
        </motion.div>
      )}

      {result && (
        <div
          className={`rounded-2xl border px-4 py-3 text-sm ${
            result.failed > 0 ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900'
          }`}
          data-testid="resend-failed-result"
        >
          <strong>{result.sent}</strong> re-sent successfully
          {result.failed > 0 && (<> · <strong>{result.failed}</strong> still failed</>)}
          {' '}out of {result.candidates} candidate{result.candidates === 1 ? '' : 's'}.
        </div>
      )}
    </div>
  );
}

